import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Move, Save, X, ChevronDown, ChevronRight, GripVertical, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import * as LucideIcons from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Swal from 'sweetalert2';

interface MenuItem {
  id: number;
  name: string;
  route: string | null;
  icon: string | null;
  parent_id: number | null;
  sort_order: number;
  is_active: boolean;
  roles: string[];
  children?: MenuItem[];
}

interface MenuFormData {
  name: string;
  name_en: string;
  route: string;
  icon: string;
  parent_id: number | null;
  sort_order: number;
  roles: string[];
  is_active: boolean;
}

const MenuManagementPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [bulkRoles, setBulkRoles] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<MenuFormData>({
    name: '',
    name_en: '',
    route: '',
    icon: '',
    parent_id: null,
    sort_order: 0,
    roles: ['admin'],
    is_active: true
  });

  const availableRoles = [
    { value: 'admin', label: 'Admin', color: 'bg-primary/20 text-primary-dark' },
    { value: 'superadmin', label: 'Süper Admin', color: 'bg-red-100 text-red-800' },
    { value: 'teknisyen', label: 'Teknisyen', color: 'bg-green-100 text-green-800' },
    { value: 'calisan', label: 'Çalışan', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' }
  ];
  const availableIcons = [
    // Temel & Dashboard
    'LayoutDashboard', 'Settings', 'Home', 'Activity',
    // Kullanıcı & Organizasyon
    'Users', 'User', 'UserPlus', 'UserMinus', 'UserCheck', 'UserCog', 'UsersRound', 'Contact', 'BookUser',
    // Yapı & Lokasyon
    'Building2', 'Building', 'MapPin', 'Store', 'Warehouse', 'Factory', 'Landmark', 'Briefcase',
    // Araç & Lojistik
    'Truck', 'Package', 'Package2', 'Box', 'Boxes', 'Archive',
    // Sunucu & Altyapı
    'Server', 'Cloud', 'Database', 'Network', 'Wifi', 'Router', 'Cable',
    // Donanım
    'HardDrive', 'Cpu', 'Monitor', 'Laptop', 'Smartphone', 'Phone', 'Printer', 'Keyboard', 'MousePointer',
    // Güvenlik & Kamera
    'Camera', 'Video', 'Lock', 'Key', 'Shield',
    // Multimedya
    'Headphones', 'Speaker', 'Mic', 'Radio', 'Tv', 'Gamepad2',
    // Finans
    'CreditCard', 'DollarSign', 'Receipt', 'Wallet', 'PiggyBank', 'CircleDollarSign', 'BadgeDollarSign', 'Banknote',
    // Ölçüm & Terazi
    'Scale', 'Gauge', 'Thermometer',
    // Analiz & Raporlama
    'BarChart3', 'TrendingUp', 'FileSpreadsheet',
    // Dosya & Belge
    'FileText', 'FileCheck', 'Folder', 'FolderOpen', 'ClipboardList', 'ClipboardCheck', 'ListTodo', 'CheckSquare',
    // İletişim
    'Mail', 'Megaphone', 'MessageSquare', 'MessagesSquare', 'Send', 'Bell',
    // Arama & Navigasyon
    'Search', 'Globe', 'Link', 'ExternalLink', 'Share2', 'Navigation', 'Map', 'Route', 'Compass',
    // Zaman & Takvim
    'Calendar', 'Clock', 'Timer',
    // Uyarı & Durum
    'AlertTriangle', 'AlertCircle', 'Info', 'HelpCircle', 'CheckCircle', 'XCircle',
    // Enerji
    'Zap', 'Power', 'PlugZap', 'Battery', 'BatteryCharging', 'Lightbulb', 'Sun', 'Moon',
    // Bakım & Servis
    'Wrench', 'ShoppingCart',
    // Etiketleme
    'Tag', 'Tags', 'Bookmark', 'Star', 'Heart', 'Flag', 'Target', 'ScanLine', 'QrCode', 'Barcode',
    // Çevre
    'Droplet', 'Wind', 'Flame', 'Snowflake'
  ];

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    try {
      const response = await fetch('/api/menu-management', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMenuItems(data);
      }
    } catch (error) {
      console.error('Menü öğeleri getirme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingItem 
        ? `/api/menu-management/${editingItem.id}`
        : '/api/menu-management';
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchMenuItems();
        resetForm();
        Swal.fire({
          icon: 'success',
          title: language === 'en' ? 'Success!' : 'Başarılı!',
          text: editingItem ? t('menuMgmt.successUpdated') : t('menuMgmt.successCreated'),
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: language === 'en' ? 'Error!' : 'Hata!',
          text: t('menuMgmt.errOperation')
        });
      }
    } catch (error) {
      console.error('Menü kaydetme hatası:', error);
    }
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: language === 'en' ? 'Are you sure?' : 'Emin misiniz?',
      text: t('menuMgmt.confirmDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: language === 'en' ? 'Yes, delete!' : 'Evet, sil!',
      cancelButtonText: t('menuMgmt.cancel')
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`/api/menu-management/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        await fetchMenuItems();
        Swal.fire({
          icon: 'success',
          title: language === 'en' ? 'Deleted!' : 'Silindi!',
          text: t('menuMgmt.successDeleted'),
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        const errorData = await response.json();
        Swal.fire({
          icon: 'error',
          title: language === 'en' ? 'Delete Failed!' : 'Silme Başarısız!',
          text: errorData.error || t('menuMgmt.errDelete')
        });
      }
    } catch (error) {
      console.error('Menü silme hatası:', error);
      Swal.fire({
        icon: 'error',
        title: language === 'en' ? 'Error!' : 'Hata!',
        text: language === 'en' ? 'An error occurred' : 'Bir hata oluştu'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      name_en: '',
      route: '',
      icon: '',
      parent_id: null,
      sort_order: 0,
      roles: ['admin'],
      is_active: true
    });
    setEditingItem(null);
    setShowForm(false);
  };

  const handleBulkRoleUpdate = async () => {
    try {
      const updates = Array.from(selectedItems).map(id => ({
        id,
        roles: bulkRoles
      }));

      const response = await fetch('/api/menu-management/bulk-roles', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        await fetchMenuItems();
        setSelectedItems(new Set());
        setBulkRoles([]);
        setShowBulkRoleModal(false);
        Swal.fire({
          icon: 'success',
          title: language === 'en' ? 'Success!' : 'Başarılı!',
          text: t('menuMgmt.bulkRoleUpdate').replace('{count}', String(selectedItems.size)),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Toplu rol güncelleme hatası:', error);
      Swal.fire({
        icon: 'error',
        title: language === 'en' ? 'Error!' : 'Hata!',
        text: t('menuMgmt.errBulkRole')
      });
    }
  };

  const handleBackup = async () => {
    try {
      const response = await fetch('/api/menu-management/backup', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `menu_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        Swal.fire({
          icon: 'success',
          title: language === 'en' ? 'Backup Done!' : 'Yedek Alındı!',
          text: t('menuMgmt.successBackup'),
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error(language === 'en' ? 'Backup failed' : 'Yedek alınamadı');
      }
    } catch (error) {
      console.error('Menü yedekleme hatası:', error);
      Swal.fire({
        icon: 'error',
        title: language === 'en' ? 'Error!' : 'Hata!',
        text: t('menuMgmt.errBackup')
      });
    }
  };

  const startEdit = (item: MenuItem) => {
    setFormData({
      name: item.name,
      name_en: (item as any).name_en || '',
      route: item.route || '',
      icon: item.icon || '',
      parent_id: item.parent_id,
      sort_order: item.sort_order,
      roles: item.roles || ['admin'],
      is_active: item.is_active
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const toggleExpanded = (id: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = menuItems.findIndex(item => item.id === active.id);
      const newIndex = menuItems.findIndex(item => item.id === over.id);
      
      const newItems = arrayMove(menuItems, oldIndex, newIndex);
      
      // Update sort_order for all items
      const updates = newItems.map((item, index) => ({
        id: item.id,
        sort_order: index + 1,
        parent_id: item.parent_id
      }));


      try {
        const response = await fetch('/api/menu-management/reorder', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({ items: updates })
        });
        
        const responseText = await response.text();

        if (response.ok) {
          await fetchMenuItems();
          Swal.fire({
            icon: 'success',
            title: language === 'en' ? 'Success!' : 'Başarılı!',
            text: t('menuMgmt.successReorder'),
            timer: 1500,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Sıralama hatası:', error);
      }
    }
  };

  const SortableMenuItem = ({ item, level = 0 }: { item: MenuItem; level?: number }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`border rounded-lg mb-2 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow ${
          isDragging ? 'shadow-lg opacity-50' : ''
        }`}
      >
        <div
          className={`flex items-start gap-2 p-3 sm:p-4 ${level > 0 ? 'border-l-4 border-primary/30' : ''}`}
          style={{ paddingLeft: `${12 + level * 20}px` }}
        >
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600 dark:text-gray-400 shrink-0 mt-1 touch-none"
          >
            <GripVertical size={16} />
          </div>

          {/* Expand button */}
          {hasChildren ? (
            <button
              onClick={() => toggleExpanded(item.id)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 shrink-0 mt-1"
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <div className="w-4 shrink-0" />
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Name + icon row */}
            <div className="flex items-center gap-2 flex-wrap">
              {item.icon && (
                <span className="text-primary text-xs font-mono shrink-0">{item.icon}</span>
              )}
              <span className="font-medium text-gray-900 dark:text-gray-100 break-words">{item.name}</span>
              {!item.is_active && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded shrink-0">
                  {t('menuMgmt.passive')}
                </span>
              )}
            </div>

            {/* Route */}
            {item.route && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">
                {item.route}
              </div>
            )}

            {/* Role badges */}
            {item.roles && item.roles.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {item.roles.map(role => {
                  const roleConfig = availableRoles.find(r => r.value === role);
                  const roleColor = roleConfig?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
                  const roleLabel = roleConfig?.label || role;
                  return (
                    <span key={role} className={`px-2 py-0.5 text-xs rounded-full font-medium ${roleColor}`}>
                      {roleLabel}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => startEdit(item)}
              className="text-primary hover:text-primary-dark p-1.5 rounded hover:bg-primary/10 min-w-[32px] min-h-[32px] flex items-center justify-center"
            >
              <Edit size={15} />
            </button>
            <button
              onClick={() => handleDelete(item.id)}
              className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50 min-w-[32px] min-h-[32px] flex items-center justify-center"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="border-t bg-gray-50 dark:bg-gray-700">
            {item.children?.map((child) => (
              <SortableMenuItem key={child.id} item={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const getFlatMenuItems = (items: MenuItem[]): MenuItem[] => {
    const flat: MenuItem[] = [];
    const traverse = (menuItems: MenuItem[]) => {
      menuItems.forEach(item => {
        flat.push(item);
        if (item.children) {
          traverse(item.children);
        }
      });
    };
    traverse(items);
    return flat;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">{t('menuMgmt.title')}</h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">{t('menuMgmt.subtitle')}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleBackup}
              className="bg-green-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2 shadow-lg transition-all min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
              title={t('menuMgmt.backup')}
            >
              <Download size={20} />
              <span>{t('menuMgmt.backup')}</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-primary to-primary-dark text-white px-4 sm:px-6 py-3 rounded-lg hover:from-primary-dark hover:to-primary-dark flex items-center justify-center space-x-2 shadow-lg transition-all min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
            >
              <Plus size={20} />
              <span>{t('menuMgmt.newMenu')}</span>
            </button>
          </div>
        </div>

      {/* Menü Listesi */}
      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={menuItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {menuItems.map((item) => (
                <SortableMenuItem key={item.id} item={item} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-6 w-full sm:max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingItem ? t('menuMgmt.formEditTitle') : t('menuMgmt.formNewTitle')}
              </h2>
              <button onClick={resetForm} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 p-1">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('menuMgmt.fieldNameTR')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 min-h-[44px] focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Türkçe ad"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('menuMgmt.fieldNameEN')}
                  </label>
                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 min-h-[44px] focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="English name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Route (URL)
                </label>
                <input
                  type="text"
                  value={formData.route}
                  onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 min-h-[44px] focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="/example-page"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('menuMgmt.fieldIcon')}
                </label>
                <div className="relative">
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 pl-10 min-h-[44px] focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">{t('menuMgmt.selectIcon')}</option>
                    {availableIcons.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                  {formData.icon && (LucideIcons as any)[formData.icon] && (
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      {React.createElement((LucideIcons as any)[formData.icon], { className: 'w-4 h-4 text-gray-600 dark:text-gray-400' })}
                    </div>
                  )}
                </div>
                {/* İkon Önizleme Grid */}
                <div className="mt-2 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  <div className="grid grid-cols-8 sm:grid-cols-10 gap-1">
                    {availableIcons.map(icon => {
                      const IconComponent = (LucideIcons as any)[icon];
                      if (!IconComponent) return null;
                      return (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon })}
                          className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors ${
                            formData.icon === icon ? 'bg-primary/20 ring-2 ring-primary' : ''
                          }`}
                          title={icon}
                        >
                          <IconComponent className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('menuMgmt.fieldParent')}
                </label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    parent_id: e.target.value ? parseInt(e.target.value) : null
                  })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 min-h-[44px] focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">{t('menuMgmt.mainMenu')}</option>
                  {getFlatMenuItems(menuItems)
                    .filter(item => item.id !== editingItem?.id)
                    .map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('menuMgmt.fieldOrder')}
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 min-h-[44px] focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('menuMgmt.fieldRoles')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {availableRoles.map(role => (
                    <label key={role.value} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ 
                              ...formData, 
                              roles: [...formData.roles, role.value] 
                            });
                          } else {
                            setFormData({ 
                              ...formData, 
                              roles: formData.roles.filter(r => r !== role.value) 
                            });
                          }
                        }}
                        className="mr-3 h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-gray-600 rounded"
                      />
                      <div className="flex items-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${role.color}`}>
                          {role.label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <span>{t('menuMgmt.fieldActive')}</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4 pb-2">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-dark flex items-center justify-center gap-2 min-h-[44px] text-sm sm:text-base font-medium"
                >
                  <Save size={16} />
                  <span>{t('menuMgmt.save')}</span>
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px] text-sm sm:text-base font-medium"
                >
                  {t('menuMgmt.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagementPage;